import { t } from "@lingui/macro";
import { OptimizedDataDto } from "@reactive-resume/dto";
import {
  ArrowUp,
  ArrowDown,
  CaretUp,
  ArrowFatUp,
  ArrowFatLinesUp,
  CaretDoubleUp,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { MetricKeyEnum } from "@reactive-resume/schema";

interface CompareProps {
  optimizationData: OptimizedDataDto;
}

const formatScore = (score: number | undefined, weight?: number) => {
  if (score === undefined) return "N/A";
  // Calculate actual score by dividing weightedScore by weight (same as metrics-bar-chart.tsx)
  const actualScore = weight && weight > 0 ? score / weight : score;
  return (actualScore * 100).toFixed(1);
};

function renderScore(currentScore: number | undefined, previousScore: number | undefined, totalWeight?: number) {
  if (currentScore === undefined || previousScore === undefined) {
    return "N/A";
  }
  const diff = (currentScore - previousScore) * 100;
  if (Math.abs(diff) < 0.1) {
    // No meaningful change - show single score
    return `${formatScore(currentScore, totalWeight)}%`;
  } else {
    // Show before → after format
    return (
      <div className="flex items-center gap-2 text-lg">
        <span>{formatScore(previousScore, totalWeight)}%</span>
        <span className="text-white/40">→</span>
        <span
          className={
            diff > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
          }
        >
          {formatScore(currentScore, totalWeight)}%
        </span>
      </div>
    );
  }
}

const OverallScore = ({ analysis, initialAnalysis }: { analysis: any; initialAnalysis: any }) => {
  // Calculate total weight from all metrics (same approach as metrics-bar-chart.tsx)
  const totalWeight = analysis?.metrics 
    ? Object.values(analysis.metrics).reduce((sum: number, metric: any) => sum + (metric.weight || 0), 0)
    : 1;
  
  const currentScore = analysis.overallWeightedScore;
  const previousScore = initialAnalysis.overallWeightedScore;
  
  // Calculate actual scores by dividing by total weight
  const currentActualScore = currentScore !== undefined ? currentScore / totalWeight : undefined;
  const previousActualScore = previousScore !== undefined ? previousScore / totalWeight : undefined;
  
  const diff =
    currentActualScore !== undefined && previousActualScore !== undefined
      ? (currentActualScore - previousActualScore) * 100
      : undefined;

  const renderClasses = (currentActual: number | undefined, previousActual: number | undefined) => {
    if (currentActual === undefined || previousActual === undefined) {
      return "bg-white dark:bg-gray-800";
    }
    const diff = (currentActual - previousActual) * 100;
    const hasMeaningfulChange = Math.abs(diff) >= 0.1;
    if (hasMeaningfulChange && diff > 0) {
      return "border-green-200 bg-green-50 dark:border-green-800/30 dark:bg-green-900/20";
    } else if (hasMeaningfulChange && diff < 0) {
      return "border-red-200 bg-red-50 dark:border-red-800/30 dark:bg-red-900/20";
    } else {
      return "bg-white dark:bg-gray-800";
    }
  };

  const renderDiffIndicator = () => {
    if (
      currentActualScore === undefined ||
      previousActualScore === undefined ||
      diff === undefined ||
      Math.abs(diff) < 0.1
    ) {
      return null;
    }

    if (diff > 0) {
      return (
        <div className="relative top-2 flex flex-col items-center justify-center text-5xl font-semibold text-green-600 dark:text-green-400 [&_svg]:absolute [&_svg]:top-[-15px] [&_svg]:h-7 [&_svg]:w-7 [&_svg]:opacity-40">
          <div className="absolute top-[0px] left-[-10px] opacity-50">
            <ArrowUp className="left-[0px]" weight="thin" />
            <ArrowUp className="left-[30px]" weight="thin" />
            <ArrowUp className="left-[60px]" weight="thin" />
            <ArrowFatLinesUp className="left-[90px]" weight="thin" />
            <ArrowFatLinesUp className="left-[120px]" weight="thin" />
            <ArrowFatUp className="left-[150px]" weight="thin" />
            <ArrowUp className="left-[180px]" weight="thin" />
            <ArrowUp className="left-[210px]" weight="thin" />
            <ArrowUp className="left-[240px]" weight="thin" />
            <ArrowUp className="left-[270px]" weight="thin" />
            <ArrowUp className="left-[300px]" weight="thin" />
            <CaretUp className="left-[330px]" weight="thin" />
            <ArrowUp className="left-[360px]" weight="thin" />
            <ArrowUp className="left-[390px]" weight="thin" />
            <ArrowUp className="left-[420px]" weight="thin" />
            <ArrowUp className="left-[450px]" weight="thin" />
            <ArrowUp className="left-[480px]" weight="thin" />
            <ArrowUp className="left-[510px]" weight="thin" />
          </div>
          <div className="absolute top-[-33px] left-[-23px] opacity-40">
            <CaretUp className="left-[0px]" weight="thin" />
            <CaretUp className="left-[30px]" weight="thin" />
            <CaretDoubleUp className="left-[60px]" weight="thin" />
            <CaretDoubleUp className="left-[90px]" weight="thin" />
            <CaretDoubleUp className="left-[120px]" weight="thin" />
            <CaretUp className="left-[150px]" weight="thin" />
            <CaretDoubleUp className="left-[180px]" weight="thin" />
            <ArrowUp className="left-[210px]" weight="thin" />
            <ArrowUp className="left-[240px]" weight="thin" />
            <CaretUp className="left-[270px]" weight="thin" />
            <CaretDoubleUp className="left-[300px]" weight="thin" />
            <CaretUp className="left-[330px]" weight="thin" />
            <CaretUp className="left-[360px]" weight="thin" />
            <CaretUp className="left-[390px]" weight="thin" />
            <CaretUp className="left-[420px]" weight="thin" />
            <CaretUp className="left-[450px]" weight="thin" />
            <CaretUp className="left-[480px]" weight="thin" />
            <CaretUp className="left-[510px]" weight="thin" />
          </div>
          <div className="mt-12 mb-6 flex flex-col items-center justify-center gap-1">
            <div>+{diff.toFixed(1)}%</div>
            {/*<div className="text-base">{t`Increased`}</div>*/}
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex items-center text-xl font-semibold text-red-600 dark:text-red-400">
          <ArrowDown className="mr-1" weight="bold" />
          <span>{diff.toFixed(1)}%</span>
        </div>
      );
    }
  };

  return (
    <div
      className={`h-full overflow-hidden rounded-lg border p-4 pt-8 shadow-sm ${renderClasses(currentActualScore, previousActualScore)}`}
    >
      {renderDiffIndicator()}

      <div className="flex items-center justify-center">
        <div className="text-2xl font-bold">{renderScore(currentActualScore, previousActualScore)}</div>
      </div>
      <div className="mt-1 text-center text-lg font-semibold">{t`Overall Score`}</div>
    </div>
  );
};

const IndividualMetrics = ({
  metricKeys,
  initialAnalysis,
  analysis,
}: {
  metricKeys: string[];
  initialAnalysis: any;
  analysis: any;
}) => {
  type IndividualMetric = {
    key: string;
    initialMetric: any;
    currentMetric: any;
    diff: number;
    isImproved: boolean;
    hasMeaningfulChange: boolean;
    diffDisplay: number;
  };
  const changedMetrics = metricKeys
    .map((key) => {
      const initialMetric = initialAnalysis?.metrics?.[key as MetricKeyEnum];
      const currentMetric = analysis.metrics[key as MetricKeyEnum];

      if (!initialMetric || !currentMetric) {
        return null;
      }

      // Calculate actual scores first (same as metrics-bar-chart.tsx)
      const currentActualScore = currentMetric.weight > 0 ? currentMetric.weightedScore! / currentMetric.weight : currentMetric.weightedScore!;
      const initialActualScore = initialMetric.weight > 0 ? initialMetric.weightedScore! / initialMetric.weight : initialMetric.weightedScore!;
      
      const diff = currentActualScore - initialActualScore;
      const hasMeaningfulChange = Math.abs(diff) >= 0.001;

      // Only return metrics that have meaningful changes
      if (!hasMeaningfulChange) {
        return null;
      }

      const isImproved = diff > 0;
      const diffDisplay = Math.round(diff * 1000) / 10;

      // Hide negative metrics unless they are greater than 5%
      if (!isImproved && Math.abs(diffDisplay) <= 5) {
        return null;
      }

      return {
        key,
        initialMetric,
        currentMetric,
        diff,
        isImproved,
        hasMeaningfulChange,
        diffDisplay,
      };
    })
    .filter((item) => item !== null) as Array<IndividualMetric>;

  if (changedMetrics.length === 0) {
    return (
      <div className="rounded-md border bg-gray-50 p-3 text-center text-sm text-gray-600 dark:bg-gray-800 dark:text-gray-400">
        {t`No significant changes in individual metrics`}
      </div>
    );
  }

  return changedMetrics
    .slice(0, 9)
    .map(
      ({
        key,
        initialMetric,
        currentMetric,
        diff,
        isImproved,
        hasMeaningfulChange,
        diffDisplay,
      }) => {
        return (
          <div
            key={key}
            className={`rounded-md border p-2.5 text-sm ${
              hasMeaningfulChange && isImproved
                ? "border-green-200 bg-green-50 dark:border-green-800/30 dark:bg-green-900/20"
                : hasMeaningfulChange && diff < 0
                  ? "border-red-200 bg-red-50 dark:border-red-800/30 dark:bg-red-900/20"
                  : "bg-white dark:bg-gray-800"
            }`}
          >
            <div className="justify-arround relative flex flex-col items-center">
              <span className="font-medium">{currentMetric.metric}</span>
              <div className="flex items-center gap-2 text-xs">
                {Math.abs(diff) < 0.001 ? (
                  // No meaningful change - show single score
                  <span>{formatScore(currentMetric.weightedScore, currentMetric.weight)}%</span>
                ) : (
                  // Show before → after with change indicator
                  <>
                    <div>
                      <span>{formatScore(initialMetric.weightedScore, initialMetric.weight)}%</span>
                      <span className="text-white/40">→</span>
                      <span
                        className={
                          isImproved
                            ? "text-green-600 dark:text-green-400"
                            : "text-red-600 dark:text-red-400"
                        }
                      >
                        {formatScore(currentMetric.weightedScore, currentMetric.weight)}%
                      </span>
                    </div>
                    <span
                      className={`[&_span]:text-md! flex items-center text-lg font-bold ${
                        isImproved
                          ? "text-green-600 dark:text-green-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {isImproved ? (
                        <>
                          +{diffDisplay % 1 === 0 ? diffDisplay.toFixed(0) : diffDisplay.toFixed(1)}
                          <span>%</span>
                          <ArrowUp className="absolute right-2" size={12} weight="bold" />
                        </>
                      ) : (
                        <>
                          <ArrowDown className="absolute right-2" size={12} weight="bold" />
                          {diffDisplay % 1 === 0 ? diffDisplay.toFixed(0) : diffDisplay.toFixed(1)}
                          <span>%</span>
                        </>
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      },
    );
};

const NoAnalysisData = () => (
  <div className="rounded-lg border bg-red-50 p-4 text-sm dark:bg-red-900/20">
    <div className="font-semibold text-red-800 dark:text-red-200">No Analysis Data Available</div>
    <div className="text-red-600 dark:text-red-300">
      Check the console for debugging information about the data structure.
    </div>
  </div>
);

export const CompareMetrics = ({ optimizationData }: CompareProps) => {
  const initialAnalysis = optimizationData?.initialAnalysis;
  const analysis = optimizationData?.analysis;

  const hasAnalysisData = Boolean(
    initialAnalysis && analysis && initialAnalysis.metrics && analysis.metrics,
  );

  const metricKeys = hasAnalysisData
    ? Object.keys(analysis.metrics)
        .filter((key) => Object.values(MetricKeyEnum).includes(key as MetricKeyEnum))
        .sort((a, b) => {
          const metricA = analysis.metrics?.[a as MetricKeyEnum];
          const metricB = analysis.metrics?.[b as MetricKeyEnum];
          const initialMetricA = initialAnalysis.metrics?.[a as MetricKeyEnum];
          const initialMetricB = initialAnalysis.metrics?.[b as MetricKeyEnum];

          if (!metricA || !metricB || !initialMetricA || !initialMetricB) return 0;
          if (
            metricA.weightedScore === undefined ||
            metricB.weightedScore === undefined ||
            initialMetricA.weightedScore === undefined ||
            initialMetricB.weightedScore === undefined
          )
            return 0;

          const diffA = metricA.weightedScore - initialMetricA.weightedScore;
          const diffB = metricB.weightedScore - initialMetricB.weightedScore;

          return diffB - diffA; // Descending order (most improved first)
        })
    : [];

  return (
    <motion.div
      className={`w-full space-y-3 overflow-auto bg-gray-50/50 p-4 2xl:px-0 dark:bg-gray-900/20`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut", delay: 0.4 }}
    >
      <div className="grid grid-cols-6 items-center justify-between gap-2">
        <div className="col-span-2 h-full 2xl:col-span-6">
          {hasAnalysisData ? (
            <OverallScore analysis={analysis} initialAnalysis={initialAnalysis} />
          ) : (
            <NoAnalysisData />
          )}
        </div>
        <div className="col-span-4 2xl:col-span-6">
          {hasAnalysisData && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-1">
                <IndividualMetrics
                  metricKeys={metricKeys}
                  initialAnalysis={initialAnalysis}
                  analysis={analysis}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
